import { Tag } from "@/src/components/Tag";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import {
  SettingsCard,
  SettingsEmpty,
  SettingsRow,
} from "@/src/components/settings/SettingsLayout";
import { Button as LegacyButton } from "@/src/components/tailwind/Button";
import { TextArea } from "@/src/components/tailwind/TextArea";
import { TextInput } from "@/src/components/tailwind/TextInput";
import { toasts } from "@/src/components/toasts/toasts";
import { Button } from "@/src/components/ui/button";
import {
  CreateDataRoomPermissionType,
  FirmQuery,
  useCreateDataRoomPermissionMutation,
  useDeleteDataRoomPermissionMutation,
  useUpdateDataRoomPermissionMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useState } from "react";

type Permission =
  FirmQuery["firm"]["settings"]["dealSettings"]["dataRoomPermissions"][0];

const PERMISSION_COLOR_CYCLE = [
  "persian",
  "green",
  "red",
  "blue",
  "yellow",
] as const;

function permissionTagColor(accessLevel: number) {
  return PERMISSION_COLOR_CYCLE[
    (accessLevel - 1) % PERMISSION_COLOR_CYCLE.length
  ];
}

export function DataRoomPermissions(props: {
  firm: FirmQuery["firm"];
}) {
  const [showCreate, setShowCreate] = useState(false);
  const permissions = [...props.firm.settings.dealSettings.dataRoomPermissions].sort(
    (a, b) => a.accessLevel - b.accessLevel,
  );

  return (
    <>
      <SettingsCard
        title="Permission levels"
        description="Default access tiers for documents in new deals."
        action={
          <Button type="button" size="sm" onClick={() => setShowCreate(true)}>
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            Create
          </Button>
        }
        contentClassName="divide-y divide-border p-0"
      >
        {permissions.length === 0 ? (
          <SettingsEmpty>No permission levels configured.</SettingsEmpty>
        ) : (
          permissions.map((p) => (
            <PermissionLevelRow
              key={p.id}
              permission={p}
              firmID={props.firm.id}
            />
          ))
        )}
      </SettingsCard>

      <CreateDataRoomPermissionModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        firm={props.firm}
      />
    </>
  );
}

function PermissionLevelRow(props: {
  permission: Permission;
  firmID: string;
}) {
  const { permission: p } = props;
  const [modal, setModal] = useState<"" | "edit" | "delete">("");

  return (
    <>
      <SettingsRow>
        <div className="min-w-0 space-y-1">
          <Tag color={permissionTagColor(p.accessLevel)} text={p.name} />
          {p.description ? (
            <p className="text-sm text-muted-foreground">{p.description}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setModal("edit")}
          >
            Edit
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setModal("delete")}
            className="text-destructive hover:text-destructive"
          >
            Delete
          </Button>
        </div>
      </SettingsRow>

      <EditDataRoomPermissionModal
        open={modal === "edit"}
        onClose={() => setModal("")}
        permission={p}
        firmID={props.firmID}
      />
      <DeleteDataRoomPermissionModal
        open={modal === "delete"}
        onClose={() => setModal("")}
        permission={p}
        firmID={props.firmID}
      />
    </>
  );
}

function EditDataRoomPermissionModal(props: {
  open: boolean;
  onClose: () => void;
  permission: Permission;
  firmID: string;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateDataRoomPermission = useUpdateDataRoomPermissionMutation(client);

  const [name, setName] = useState(props.permission.name);
  const [description, setDescription] = useState(props.permission.description);

  const { open, onClose, permission } = props;

  return (
    <AnimatedModal open={open} onClose={onClose} size="lg">
      <div>
        <div className="flex items-center justify-between">
          <p className="font-semibold text-foreground">Edit permission level</p>
          <CloseIcon onClose={onClose} />
        </div>

        <TextInput
          margin="s 0 0 0"
          label="Name"
          placeholder="Phase 1"
          secondaryLabel=""
          value={name}
          onChange={(e) => {
            setName(e.currentTarget.value);
          }}
        />

        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm leading-8 font-semibold text-foreground">
            Description
          </p>
          <p className="text-xs text-muted-foreground">Optional</p>
        </div>
        <TextArea
          label="Description"
          placeholder=""
          name="description"
          rows={2}
          value={description}
          onChange={(e) => {
            setDescription(e.currentTarget.value);
          }}
        />

        <div className="mt-6 flex justify-end">
          <LegacyButton
            text="Save"
            variant="positive"
            isLoading={updateDataRoomPermission.isPending}
            onClick={() => {
              if (updateDataRoomPermission.isPending) {
                return;
              }

              updateDataRoomPermission.mutate(
                {
                  id: permission.id,
                  name,
                  description,
                },
                {
                  onSuccess: () => {
                    toasts.success("Permission updated");
                    queryClient.invalidateQueries({
                      queryKey: ["Firm", { id: props.firmID }],
                    });
                    onClose();
                  },
                  onError: () => {
                    toasts.error("Failed to update permission");
                  },
                },
              );
            }}
          />
        </div>
      </div>
    </AnimatedModal>
  );
}

function CreateDataRoomPermissionModal(props: {
  open: boolean;
  onClose: () => void;
  firm: FirmQuery["firm"];
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const createDataRoomPermission = useCreateDataRoomPermissionMutation(client);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const { open, onClose, firm } = props;

  function handleClose() {
    setName("");
    setDescription("");
    onClose();
  }

  return (
    <AnimatedModal open={open} onClose={handleClose} size="lg">
      <div>
        <div className="flex items-center justify-between">
          <p className="font-semibold text-foreground">
            Create permission level
          </p>
          <CloseIcon onClose={handleClose} />
        </div>

        <TextInput
          margin="s 0 0 0"
          label="Name"
          placeholder="Phase 1"
          secondaryLabel=""
          value={name}
          onChange={(e) => {
            setName(e.currentTarget.value);
          }}
        />

        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm leading-8 font-semibold text-foreground">
            Description
          </p>
          <p className="text-xs text-muted-foreground">Optional</p>
        </div>
        <TextArea
          label="Description"
          placeholder=""
          name="description"
          rows={2}
          value={description}
          onChange={(e) => {
            setDescription(e.currentTarget.value);
          }}
        />

        <div className="mt-6 flex justify-end">
          <LegacyButton
            text="Create"
            variant="positive"
            isLoading={createDataRoomPermission.isPending}
            onClick={() => {
              if (createDataRoomPermission.isPending) {
                return;
              }

              createDataRoomPermission.mutate(
                {
                  firmID: firm.id,
                  name,
                  description,
                  accessLevel:
                    firm.settings.dealSettings.dataRoomPermissions.length + 1,
                  type: CreateDataRoomPermissionType.FirmTemplate,
                },
                {
                  onSuccess: () => {
                    toasts.success("Permission created");
                    queryClient.invalidateQueries({
                      queryKey: ["Firm", { id: props.firm.id }],
                    });
                    handleClose();
                  },
                  onError: () => {
                    toasts.error("Failed to create permission");
                  },
                },
              );
            }}
          />
        </div>
      </div>
    </AnimatedModal>
  );
}

function DeleteDataRoomPermissionModal(props: {
  open: boolean;
  onClose: () => void;
  permission: Permission;
  firmID: string;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const deleteDataRoomPermission = useDeleteDataRoomPermissionMutation(client);

  const [confirmationName, setConfirmationName] = useState("");

  const { open, onClose, permission } = props;

  function handleClose() {
    setConfirmationName("");
    onClose();
  }

  const isConfirmationValid = confirmationName === permission.name;

  return (
    <AnimatedModal open={open} onClose={handleClose} size="lg">
      <div>
        <div className="flex items-center justify-between">
          <p className="font-semibold text-foreground">
            Delete permission level
          </p>
          <CloseIcon onClose={handleClose} />
        </div>

        <div className="mt-4">
          <p className="text-sm text-muted-foreground mb-4">
            This action cannot be undone. This will permanently delete the "
            {permission.name}" permission.
          </p>

          <p className="text-sm text-muted-foreground mb-2">
            Please type{" "}
            <span className="font-semibold text-foreground">
              {permission.name}
            </span>{" "}
            to confirm:
          </p>

          <TextInput
            margin="0"
            label=""
            placeholder={permission.name}
            secondaryLabel=""
            value={confirmationName}
            onChange={(e) => {
              setConfirmationName(e.currentTarget.value);
            }}
          />
        </div>

        <div className="mt-6 flex justify-end">
          <LegacyButton
            text="Delete permission"
            variant="danger"
            isLoading={deleteDataRoomPermission.isPending}
            isDisabled={!isConfirmationValid}
            onClick={() => {
              if (deleteDataRoomPermission.isPending || !isConfirmationValid) {
                return;
              }

              deleteDataRoomPermission.mutate(
                {
                  id: permission.id,
                },
                {
                  onSuccess: () => {
                    toasts.success("Permission deleted");
                    queryClient.invalidateQueries({
                      queryKey: ["Firm", { id: props.firmID }],
                    });
                    handleClose();
                  },
                  onError: () => {
                    toasts.error("Failed to delete permission");
                  },
                },
              );
            }}
          />
        </div>
      </div>
    </AnimatedModal>
  );
}
